import type { ExplicitType } from "../../../utils";
import { isOfType } from "../../../utils";
import type { IAttributeProperty } from "../attributes";
import type { IFieldMappingPointer, ILinkByUid } from "../utils";

import type {
  IIngredientQuantities,
  IIngredientSpec,
  IMaterialSpec,
  IMeasurementSpec,
  IProcessSpec,
  ISpecObject,
  ISpecObjectBase,
} from "./specs";

/**
 * An representation of an object from Material History data
 */
interface IRunObjectBase<
  SpecShape extends ISpecObject,
> extends ISpecObjectBase {
  field_pointer?: IFieldMappingPointer;
  spec: ILinkByUid | SpecShape;
}

export interface IIngredientRun
  extends IRunObjectBase<IIngredientSpec>, IIngredientQuantities {
  type: "ingredient_run";
  conditions?: never;
  parameters?: never;
  properties?: never;
  labels?: string[];
  material: ILinkByUid;
  process: ILinkByUid;
}

export interface IMaterialRun extends IRunObjectBase<IMaterialSpec> {
  type: "material_run";
  conditions?: never;
  parameters?: never;
  properties?: never;
  sample_type?: "experimental" | "virtual" | "production" | "unknown";
  process: ILinkByUid | IProcessRun;
  measurements?: string[];
}

export interface IMeasurementRun extends IRunObjectBase<IMeasurementSpec> {
  type: "measurement_run";
  properties: IAttributeProperty[];
  material: ILinkByUid;
}

export interface IProcessRun extends IRunObjectBase<IProcessSpec> {
  type: "process_run";
  properties?: never;
  output_material?: ILinkByUid;
  source?: {
    type: "performed_source";
    performed_by: string;
    performed_date: string;
  };
}

export type IRunObject =
  IIngredientRun | IMaterialRun | IMeasurementRun | IProcessRun;
export type RunObjectType = IRunObject["type"];

export const isIngredientRun = isOfType<IIngredientRun>("ingredient_run");
export const isMaterialRun = isOfType<IMaterialRun>("material_run");
export const isMeasurementRun = isOfType<IMeasurementRun>("measurement_run");
export const isProcessRun = isOfType<IProcessRun>("process_run");

export const isRunObject = (obj?: ExplicitType<string>): obj is IRunObject =>
  isIngredientRun(obj) ||
  isMaterialRun(obj) ||
  isMeasurementRun(obj) ||
  isProcessRun(obj);
